/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) 2021 Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React from 'react';
import EntryContentDataViewStyles from './EntryContentDataViewStyles';
import { observer } from 'mobx-react';
import { useViewModel } from '../../../../../../../hooks/useViewModel';
import { Box, IconButton, TextField, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { ElementsView } from '../../../../../../../components/Elements/ElementsView';
import { Entry } from '../../../../../../../types/course/Entry';
import { Map } from '@material-ui/icons';
import { ELEMENTS_TYPE } from '../../../../../../../utils/elementFactory';
import { EntryContentViewModel } from '../EntryContentViewModel';

interface Props {
	entry: Entry;
}

export const EntryContentDataView = observer((props: Props) => {
	const classes = EntryContentDataViewStyles();
	const viewModel: EntryContentViewModel = useViewModel(EntryContentViewModel);
	const { entry } = props;

	return (
		<Box className={classes.contentBox}>
			<Typography className={classes.locationTitle} variant={'h3'}>
				<FormattedMessage id={'entries.location'} />
			</Typography>
			<Box className={classes.locationContainer}>
				<Box>
					<Typography className={classes.contentText}>
						<FormattedMessage id={'entries.lat'} />
						{entry.lat}
					</Typography>
					<Typography className={classes.contentText}>
						<FormattedMessage id={'entries.long'} />
						{entry.long}
					</Typography>
				</Box>
				<IconButton className={classes.locationButton} onClick={() => viewModel.redirectToMaps(entry.lat, entry.long)}>
					<Map className={classes.locationIcon} />
				</IconButton>
			</Box>
			<Typography className={classes.contentTitle} variant={'h3'}>
				<FormattedMessage id={'entries.module'} />
			</Typography>
			<Typography className={classes.contentText}>
				<FormattedMessage id={'entries.moduleName'} />
				{': ' + entry.module + '\n'}
			</Typography>
			<Typography className={classes.contentText}>
				<FormattedMessage id={'entries.attempts'} />
				{': ' + entry.attempts}
			</Typography>
			<Typography className={classes.contentTitle} variant={'h3'}>
				<FormattedMessage id={'entries.comment'} />
			</Typography>
			<Typography className={classes.contentText}>{entry.note ?? '-'}</Typography>

			{!viewModel.isPublishingView() && (
				<>
					<Typography className={classes.contentTitle} variant={'h3'}>
						<FormattedMessage id={'entries.feedback'} />
					</Typography>
					<TextField
						className={classes.textField}
						variant={'outlined'}
						disabled={entry.state === 'approved'}
						fullWidth
						multiline
						rows={2}
						rowsMax={6}
						value={viewModel.getFeedback() ?? ''}
						onChange={(event) => {
							viewModel.onFeedbackChange(event);
						}}
					/>
				</>
			)}
			{!!entry.elements && !viewModel.isPublishingView() && (
				<ElementsView elements={entry.elements} type={ELEMENTS_TYPE.ENTRY} />
			)}
		</Box>
	);
});
